/**
 * dragCell —— 子项拖拽手柄格（原生 HTML5 DnD）。
 *
 * 【为何单独成文件】机制与 React 受控控件完全不同：靠 `dragstart/dragover/drop` 三个原生事件
 * + `document.createElement` 造离屏 ghost 元素 + 直接操作 DOM 类做目标行高亮。
 * 与 `renderCells` 里的受控控件没有任何共享心智模型。
 */
import React from 'react';
import { EllipsisDoubleVertical } from '@bedrock/icons-react';
import { subItemRowStyle } from './renderCells';
import type { ChildBase, RowId } from './types';

/** 拖拽跟随预览（drag image 快照）的 DOM 类名 */
export const DRAG_GHOST_CLASS = 'pro-table-drag-ghost';

/**
 * 渲染拖拽手柄子列 cell：纵向排该 subArray 全部子项的拖拽手柄，原生 HTML5 DnD
 * （dragstart 记录源下标 → dragover 允许放置 → drop 提交 reorder）。
 * 子项实际顺序由共享的 items 数组承载，拖拽只驱动该数组重排，天然与其余子列（内容/操作等）保持一致。
 *
 * 拖拽可辨认性：提供 getDragLabel 时，dragstart 把整行信息文案渲染为离屏快照元素
 * 并 dataTransfer.setDragImage —— 鼠标跟随的是完整条目信息而非孤零零的把手图标；
 * dragover 给目标位置行加浅色底，指示将要落入的位置。
 */
export function renderDragColCell(
  rowId: RowId,
  subKey: string,
  items: ChildBase[],
  onReorder: (rowId: RowId, subKey: string, fromIndex: number, toIndex: number) => void,
  getDragLabel?: (item: ChildBase) => string,
) {
  /** 创建拖拽跟随快照：离屏 fixed 元素（setDragImage 要求元素已渲染），同步捕获后移除 */
  const mountDragGhost = (text: string) => {
    const ghost = document.createElement('div');
    ghost.textContent = text;
    ghost.className = DRAG_GHOST_CLASS;
    Object.assign(ghost.style, {
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      maxWidth: '360px',
      padding: '6px 12px',
      background: '#fff',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
      fontSize: '12px',
      color: 'rgba(0, 0, 0, 0.88)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      zIndex: '9999',
    });
    document.body.appendChild(ghost);
    return ghost;
  };
  /** 目标行高亮（dragover 高频触发，直接操作 DOM 类，避免 setState 重渲染风暴） */
  const markTarget = (el: HTMLElement, on: boolean) => {
    el.style.backgroundColor = on ? 'rgba(0, 0, 0, 0.04)' : '';
  };
  return (
    <div className="flex flex-col w-full">
      {items.length === 0 ? null : items.map((it, index) => (
        <div
          key={it.id}
          className="flex items-center justify-center box-border h-[40px] cursor-move"
          style={subItemRowStyle}
          draggable
          onDragStart={(e) => {
            // 先创建快照再 setData：setDragImage 要求在 dragstart 内同步调用
            if (getDragLabel) {
              const label = getDragLabel(it);
              if (label) {
                // 整行信息快照（离屏渲染）作为 drag image 跟随鼠标，
                // 对齐 tab 场景 react-sortable-hoc「整行 helper 跟随」的效果
                const ghost = mountDragGhost(label);
                try {
                  e.dataTransfer.setDragImage(ghost, 12, 12);
                } catch {
                  // 个别环境对合成 DataTransfer 不支持 setDragImage：移除快照，回退默认图标跟随
                  ghost.remove();
                }
                setTimeout(() => ghost.remove(), 0);
              }
            }
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
          }}
          onDragOver={(e) => {
            e.preventDefault();
            markTarget(e.currentTarget, true);
          }}
          onDragLeave={(e) => markTarget(e.currentTarget, false)}
          onDrop={(e) => {
            e.preventDefault();
            markTarget(e.currentTarget, false);
            const fromIndex = Number(e.dataTransfer.getData('text/plain'));
            if (Number.isNaN(fromIndex) || fromIndex === index) return;
            onReorder(rowId, subKey, fromIndex, index);
          }}
        >
          {/* 把手图标与 tab 场景权益表 DragHandle 保持一致（EllipsisDoubleVertical 16px grab） */}
          <EllipsisDoubleVertical size={16} className="cursor-grab text-[var(--text-color-3)]" />
        </div>
      ))}
    </div>
  );
}
